// Stack blur: 3-pass box blur approximating Gaussian via the central
// limit theorem (3 iterations of box blur → ~Gaussian kernel).
//
// Replaces ctx.filter = 'blur()' which uses a low-quality GPU box blur on
// iOS Safari, producing visible stair-stepping at moderate radii (8-18 px).
// This software implementation is pixel-identical across all browsers and
// operates in O(n) per pass via sliding window.

function clamped(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

function boxBlurPass(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
  horizontal: boolean
): void {
  const diameter = 2 * radius + 1;

  if (horizontal) {
    for (let y = 0; y < h; y++) {
      let r = 0, g = 0, b = 0, a = 0;
      const rowStart = y * w * 4;

      for (let x = -radius; x <= radius; x++) {
        const idx = rowStart + clamped(x, 0, w - 1) * 4;
        r += src[idx]!;
        g += src[idx + 1]!;
        b += src[idx + 2]!;
        a += src[idx + 3]!;
      }

      for (let x = 0; x < w; x++) {
        const dstIdx = rowStart + x * 4;
        dst[dstIdx] = Math.round(r / diameter);
        dst[dstIdx + 1] = Math.round(g / diameter);
        dst[dstIdx + 2] = Math.round(b / diameter);
        dst[dstIdx + 3] = Math.round(a / diameter);

        const rmIdx = rowStart + clamped(x - radius, 0, w - 1) * 4;
        r -= src[rmIdx]!;
        g -= src[rmIdx + 1]!;
        b -= src[rmIdx + 2]!;
        a -= src[rmIdx + 3]!;

        const addIdx = rowStart + clamped(x + radius + 1, 0, w - 1) * 4;
        r += src[addIdx]!;
        g += src[addIdx + 1]!;
        b += src[addIdx + 2]!;
        a += src[addIdx + 3]!;
      }
    }
  } else {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      const colOffset = x * 4;

      for (let y = -radius; y <= radius; y++) {
        const idx = clamped(y, 0, h - 1) * w * 4 + colOffset;
        r += src[idx]!;
        g += src[idx + 1]!;
        b += src[idx + 2]!;
        a += src[idx + 3]!;
      }

      for (let y = 0; y < h; y++) {
        const dstIdx = y * w * 4 + colOffset;
        dst[dstIdx] = Math.round(r / diameter);
        dst[dstIdx + 1] = Math.round(g / diameter);
        dst[dstIdx + 2] = Math.round(b / diameter);
        dst[dstIdx + 3] = Math.round(a / diameter);

        const rmIdx = clamped(y - radius, 0, h - 1) * w * 4 + colOffset;
        r -= src[rmIdx]!;
        g -= src[rmIdx + 1]!;
        b -= src[rmIdx + 2]!;
        a -= src[rmIdx + 3]!;

        const addIdx = clamped(y + radius + 1, 0, h - 1) * w * 4 + colOffset;
        r += src[addIdx]!;
        g += src[addIdx + 1]!;
        b += src[addIdx + 2]!;
        a += src[addIdx + 3]!;
      }
    }
  }
}

function stackBlurData(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number
): void {
  if (radius < 1) return;
  const br = Math.floor(radius * 0.6);
  if (br < 1) return;

  const copy = new Uint8ClampedArray(data);

  // Pass 1: horizontal
  boxBlurPass(data, copy, w, h, br, true);
  // Pass 2: vertical
  boxBlurPass(copy, data, w, h, br, false);
  // Pass 3: horizontal
  boxBlurPass(data, copy, w, h, br, true);

  data.set(copy);
}

export function stackBlur(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number
): void {
  if (radius < 1) return;
  const img = ctx.getImageData(x, y, w, h);
  stackBlurData(img.data, w, h, radius);
  ctx.putImageData(img, x, y);
}
